class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.attachUrlBasedFilterHandlers();
      });
    } else {
      this.attachUrlBasedFilterHandlers();
    }

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.allFilters = window.allFilterEntries || [];
    this.parentCollection = window.parentCollection || window.currentCollection || "";
    this.activeUrlFilters = [];
    this.isRedirecting = false;
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", (event) => {
      const el = event.target;
      const isVatToggle = el.closest('.vat-toggle-switch') !== null;
      if (isVatToggle) return;
      this.debouncedOnSubmit(event);
    });

    this.setupRemoveAllButtons();
    this.initializeCheckboxes();
    this.fixResetButtons();

    const filterContainer = document.querySelector(".facets-container");
    const selectedSlugs = this.getSelectedSlugs();
    if (selectedSlugs.length === window.maximumFilterAmount) {
      filterContainer.classList.add("facets-max-limit");
    } else {
      filterContainer.classList.remove("facets-max-limit");
    }

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  getFilterLabel(entry) {
    if (!entry) return 'Filter';

    if (entry.handle === 'brand' || entry.filter_handle === 'brand') {
      return 'Brand';
    } else if (entry.filter_handle === 'availability') {
      return 'Availability';
    } else if (entry.handle) {
      return entry.handle.charAt(0).toUpperCase() + entry.handle.slice(1).replace(/-/g, ' ');
    }
    return 'Filter';
  }

  attachUrlBasedFilterHandlers() {
    const urlBasedFilterTags = document.querySelectorAll(".js-url-based-filter");

    urlBasedFilterTags.forEach(tag => {
      if (!tag.dataset.handlerAttached) {
        tag.dataset.handlerAttached = "true";
        tag.addEventListener("click", (e) => {
          e.preventDefault();

          if (this.isRedirecting) {
            console.log("🚫 URL-based filter click blocked - redirect in progress");
            return;
          }

          const filterSlug = tag.dataset.filterSlug;
          const currentUrl = new URL(window.location.href);
          const currentParams = new URLSearchParams(currentUrl.search);

          const entry = this.allFilters.find(f => f.slug === filterSlug);
          if (entry) {
            const paramToRemove = this.getFilterParamName(entry);
            if (paramToRemove) {
              currentParams.delete(paramToRemove);
            }
          }

          const preservedParams = currentParams.toString();
          const targetUrl = `/collections/${this.parentCollection}${preservedParams ? '?' + preservedParams : ''}`;

          this.navigateToCollection(targetUrl);
        });
      }
    });
  }

  autoCheckUrlBasedFilters() {
    const currentPath = window.location.pathname;
    const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
    const parentCollection = this.parentCollection;

    if (!collectionHandle || collectionHandle === parentCollection) {
      return;
    }

    let fragments = [];

    if (collectionHandle.startsWith(parentCollection + "-")) {
      const suffix = collectionHandle.slice(parentCollection.length + 1);
      fragments = [suffix];
    } else if (collectionHandle.endsWith("-" + parentCollection)) {
      const prefix = collectionHandle.slice(0, collectionHandle.length - parentCollection.length - 1);
      fragments = [prefix];
    } else {
      return;
    }

    const allCheckboxes = document.querySelectorAll('input[data-filter-key]');

    allCheckboxes.forEach(checkbox => {
      const filterKey = checkbox.dataset.filterKey;
      const filterValues = checkbox.dataset.filterValues;

      if (!filterValues) return;

      const firstHyphenIndex = filterValues.indexOf('-');
      if (firstHyphenIndex === -1) return;

      const filterType = filterValues.substring(0, firstHyphenIndex);
      const filterValue = filterValues.substring(firstHyphenIndex + 1);

      const filterValueHandle = filterValue.toLowerCase().replace(/\s+/g, '-');

      const shouldCheck = fragments.some(fragment => fragment === filterValueHandle);

      if (shouldCheck) {
        checkbox.checked = true;
        checkbox.setAttribute('checked', 'checked');
        checkbox.removeAttribute('name');
        checkbox.setAttribute('data-hidden-from-native', 'true');
        checkbox.setAttribute('data-url-based-filter', 'true');

        const label = checkbox.closest('label');
        if (label) {
          label.classList.add('active');
        }

        const entry = this.allFilters.find(f => f.slug === filterKey);
        if (entry) {
          const existingFilter = this.activeUrlFilters.find(f => f.slug === entry.slug);
          if (!existingFilter) {
            this.activeUrlFilters.push({
              slug: entry.slug,
              label: entry.label,
              filterSlug: filterKey,
              filterEntry: entry
            });
          }
        }
      }
    });
  }

  static clearCheckedFilters() {
    if (window.location.pathname === `/collections/${window.parentCollection || 'internal-doors'}`) {
      const urlParams = new URLSearchParams(window.location.search);
      const filterParams = Array.from(urlParams.keys()).filter(key => key.startsWith('filter.'));

      const filterGroups = {};
      filterParams.forEach(param => {
        let filterType;
        if (param === 'filter.p.vendor') {
          filterType = 'vendor';
        } else if (param.startsWith('filter.p.m.custom.')) {
          filterType = param.replace('filter.p.m.custom.', '');
        } else if (param.startsWith('filter.v.')) {
          filterType = param.replace('filter.v.', '');
        } else {
          filterType = param;
        }

        if (!filterGroups[filterType]) {
          filterGroups[filterType] = [];
        }
        filterGroups[filterType].push(param);
      });

      const hasMultipleFiltersOfSameType = Object.values(filterGroups).some(group => group.length > 1);

      if (hasMultipleFiltersOfSameType) {
        return;
      }

      document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]:checked, #FacetFiltersFormMobile input[type="checkbox"]:checked').forEach(cb => {
        cb.checked = false;
        cb.removeAttribute('checked');
        cb.closest('label')?.classList.remove('active');
      });
    }
  }

  static initFormCleaner() {
    const runCleaner = () => setTimeout(FacetFiltersForm.clearCheckedFilters, 50);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runCleaner);
    } else {
      runCleaner();
    }

    document.addEventListener('visibilitychange', () => !document.hidden && runCleaner());
  }

  navigateToCollection(targetUrl) {
    this.isRedirecting = true;

    setTimeout(() => {
      this.isRedirecting = false;
    }, 2000);

    window.location.href = targetUrl;
  }

  getFilterParamName(entry) {
    if (entry.handle === 'brand') {
      return 'filter.p.vendor';
    } else if (entry.filter_handle === 'availability' || entry.handle_fragment === 'availability') {
      return 'filter.v.availability';
    } else if (entry.metafield_namespace && entry.filter_handle) {
      return `filter.p.m.${entry.metafield_namespace}.${entry.filter_handle}`;
    }
    return null;
  }

  setupRemoveAllButtons() {
    const removeAllSelectors = [
      ".active-facets__button-remove",
      ".mobile-facets__clear",
      ".js-clear-custom-filters"
    ];

    removeAllSelectors.forEach(selector => {
      const buttons = document.querySelectorAll(selector);
      buttons.forEach(clearBtn => {
        clearBtn.replaceWith(clearBtn.cloneNode(true));
      });
    });

    removeAllSelectors.forEach(selector => {
      const buttons = document.querySelectorAll(selector);
      buttons.forEach(clearBtn => {
        clearBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.customLabel = null;
          this.activeUrlFilters = [];
          this.navigateToCollection(`/collections/${this.parentCollection}`);
        });
      });
    });
  }

  hasMultipleFiltersOfSameType() {
    const urlParams = new URLSearchParams(window.location.search);
    const filterParams = Array.from(urlParams.entries()).filter(([key, value]) => {
      return key.startsWith('filter.') && value !== '';
    });

    const filterGroups = {};
    filterParams.forEach(([param, value]) => {
      let filterType;
      if (param === 'filter.p.vendor') {
        filterType = 'vendor';
      } else if (param.startsWith('filter.p.m.custom.')) {
        filterType = param.replace('filter.p.m.custom.', '');
      } else if (param.startsWith('filter.v.')) {
        filterType = param.replace('filter.v.', '').split('.')[0];
      } else {
        filterType = param;
      }

      if (!filterGroups[filterType]) {
        filterGroups[filterType] = [];
      }
      filterGroups[filterType].push(param);
    });

    return Object.values(filterGroups).some(group => group.length > 1);
  }

  initializeCheckboxes() {
    const facetForm = this.querySelector("form");
    const mobileForm = document.querySelector("#FacetFiltersFormMobile");
    this.activeUrlFilters = [];

    if (!this.hasAutoChecked) {
      this.autoCheckUrlBasedFilters();
      this.hasAutoChecked = true;
    }

    [facetForm, mobileForm].filter(Boolean).forEach(form => {
      const selectedInputs = form.querySelectorAll("input[data-filter-key]");

      selectedInputs.forEach((cb) => {
        if (!cb.dataset.listenerAttached) {
          cb.dataset.listenerAttached = "true";

          cb.addEventListener("change", (event) => {
            const checkbox = event.target;
            const slug = checkbox.dataset.filterKey;
            const entry = this.allFilters.find((f) => f.slug === slug);

            const currentCollection = window.location.pathname.split("/collections/")[1].split("?")[0];
            const isOnFacetedCollection = currentCollection !== this.parentCollection;

            if (checkbox.checked && entry?.handle_fragment && !isOnFacetedCollection) {
              if (this.hasMultipleFiltersOfSameType()) {
                return;
              }

              const currentUrl = new URL(window.location.href);
              const filterParam = this.getFilterParamName(entry);

              if (filterParam && currentUrl.searchParams.has(filterParam)) {
                return;
              }
            }

            const isUrlBasedFilter = checkbox.hasAttribute("data-hidden-from-native");
            if (!checkbox.checked && isUrlBasedFilter) {
              const currentUrl = new URL(window.location.href);
              const currentParams = new URLSearchParams(currentUrl.search);
              const targetUrl = `/collections/${this.parentCollection}?${currentParams.toString()}`;
              this.navigateToCollection(targetUrl);
              return;
            }

            const currentForm = checkbox.closest("form");
            const selectedInputs = currentForm.querySelectorAll("input:checked[data-filter-key]");
            const groups = {};
            selectedInputs.forEach((input) => {
              const slug = input.dataset.filterKey;
              const entry = this.allFilters.find((f) => f.slug === slug);
              const group = entry?.handle || "other";
              if (!groups[group]) groups[group] = [];
              groups[group].push(slug);
            });

            const hasMultipleInSameGroup = Object.values(groups).some((slugs) => slugs.length > 1);

            if (hasMultipleInSameGroup) {
              const currentCollection = window.location.pathname.split("/collections/")[1].split("?")[0];
              const parent = this.parentCollection;

              if (currentCollection !== parent) {
                const facetForm = checkbox.closest("form");
                const parent = this.parentCollection;
                const fullHandle = window.location.pathname.split("/collections/")[1] || "";
                let currentHandle = fullHandle;

                if (fullHandle.startsWith(parent + "-")) {
                  const childPart = fullHandle.slice(parent.length + 1);
                  currentHandle = [parent, childPart];
                } else if (fullHandle.endsWith("-" + parent)) {
                  const childPart = fullHandle.slice(0, fullHandle.length - parent.length - 1);
                  currentHandle = [parent, childPart];
                } else {
                  currentHandle = [fullHandle];
                }

                currentHandle.forEach((fragment) => {
                  let entry = this.allFilters.find((f) => f.handle_fragment === fragment);

                  if (!entry) {
                    entry = this.allFilters.find((f) => {
                      const filterHandle = f.filter_handle || '';
                      const handleFragment = f.handle_fragment || '';

                      return filterHandle.includes(fragment) ||
                          fragment.includes(filterHandle) ||
                          filterHandle.replace(/[^a-zA-Z0-9]/g, '').includes(fragment.replace(/[^a-zA-Z0-9]/g, '')) ||
                          fragment.replace(/[^a-zA-Z0-9]/g, '').includes(filterHandle.replace(/[^a-zA-Z0-9]/g, ''));
                    });
                  }

                  if (!entry) {
                    entry = this.allFilters.find((f) => {
                      const labelHandle = (f.label || '').toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
                      const fragmentClean = fragment.replace(/[^a-zA-Z0-9]/g, '');
                      return labelHandle.includes(fragmentClean) || fragmentClean.includes(labelHandle);
                    });
                  }

                  if (entry && entry.filter_handle) {
                    const input = document.createElement("input");
                    input.type = "hidden";

                    if (entry.handle === 'brand') {
                      input.name = `filter.p.vendor`;
                      input.value = entry.label.split(': ')[1] || entry.label;
                    } else if (entry.metafield_namespace) {
                      const cleanFilterHandle = entry.filter_handle
                          .replace(/\([^)]*\)/g, '')
                          .replace(/[^a-zA-Z0-9_]/g, '')
                          .replace(/_+/g, '_')
                          .replace(/^_|_$/g, '');

                      input.name = `filter.p.m.${entry.metafield_namespace}.${cleanFilterHandle}`;
                      input.value = entry.label.split(': ')[1] || entry.label;
                    } else {
                      return;
                    }

                    const existingInput = facetForm.querySelector(`[name="${input.name}"][value="${input.value}"]`);
                    if (existingInput) {
                      existingInput.remove();
                    }
                    facetForm.appendChild(input);
                  }
                });

                const searchParams = new URLSearchParams(new FormData(facetForm)).toString();
                const paramString = searchParams ? '?' + searchParams : '';

                checkbox.checked = false;
                checkbox.removeAttribute('checked');
                const label = checkbox.closest('label');
                if (label) {
                  label.classList.remove('active');
                }
                this.navigateToCollection(`/collections/${parent}${paramString}`);
                return;
              }
            }
          });
        }
      });

      const seoLinks = document.querySelectorAll('.facet-seo-link-main');
      seoLinks.forEach(link => {
        if (!link.dataset.handlerAttached) {
          link.dataset.handlerAttached = "true";
          link.addEventListener('click', (e) => {
            e.preventDefault();

            const linkUrl = new URL(link.href, window.location.origin);
            const targetCollection = linkUrl.pathname.split('/collections/')[1];
            const currentCollection = window.location.pathname.split('/collections/')[1]?.split('?')[0];
            const parentCollection = this.parentCollection;

            let newFilterFragment = '';
            if (targetCollection.startsWith(parentCollection + '-')) {
              newFilterFragment = targetCollection.slice(parentCollection.length + 1);
            } else if (targetCollection.endsWith('-' + parentCollection)) {
              newFilterFragment = targetCollection.slice(0, targetCollection.length - parentCollection.length - 1);
            }

            const filterEntry = this.allFilters.find(f =>
                f.handle_fragment === newFilterFragment ||
                f.label.toLowerCase().replace(/\s+/g, '-') === newFilterFragment
            );

            if (filterEntry) {
              const newFilterParam = this.getFilterParamName(filterEntry);

              // Only run conflict detection for custom metafield filters
              if (newFilterParam?.startsWith('filter.p.m.custom.')) {
                let newFilterType;
                if (newFilterParam === 'filter.p.vendor') {
                  newFilterType = 'vendor';
                } else if (newFilterParam.startsWith('filter.p.m.custom.')) {
                  newFilterType = newFilterParam.replace('filter.p.m.custom.', '');
                } else if (newFilterParam.startsWith('filter.v.')) {
                  newFilterType = newFilterParam.replace('filter.v.', '').split('.')[0];
                }

                const urlParams = new URLSearchParams(window.location.search);
                const hasConflict = Array.from(urlParams.entries()).some(([key, value]) => {
                  if (!key.startsWith('filter.') || value === '') return false;

                  let existingFilterType;
                  if (key === 'filter.p.vendor') {
                    existingFilterType = 'vendor';
                  } else if (key.startsWith('filter.p.m.custom.')) {
                    existingFilterType = key.replace('filter.p.m.custom.', '');
                  } else if (key.startsWith('filter.v.')) {
                    existingFilterType = key.replace('filter.v.', '').split('.')[0];
                  }

                  return existingFilterType === newFilterType;
                });

                if (hasConflict) {
                  const currentParams = new URLSearchParams(window.location.search);
                  let newFilterValue = filterEntry.label.split(': ')[1] || filterEntry.label;
                  if (newFilterParam === 'filter.v.availability' && newFilterValue.toLowerCase().includes('out of stock')) {
                    newFilterValue = '0';
                  }
                  currentParams.append(newFilterParam, newFilterValue);

                  const paramString = currentParams.toString() ? '?' + currentParams.toString() : '';
                  const targetUrl = `/collections/${this.parentCollection}${paramString}`;

                  this.navigateToCollection(targetUrl);
                  return;
                }
              }
            }

            const form = document.querySelector('#FacetFiltersForm') || document.querySelector('#FacetFiltersFormMobile');
            if (form) {
              const formData = new FormData(form);
              const currentParams = new URLSearchParams();

              for (const [key, value] of formData.entries()) {
                if (key !== 'utf8' && value !== '') {
                  currentParams.append(key, value);
                }
              }

              const seoUrl = new URL(link.href, window.location.origin);

              currentParams.forEach((value, key) => {
                seoUrl.searchParams.set(key, value);
              });

              window.location.href = seoUrl.href;
            } else {
              window.location.href = link.href;
            }
          });
        }
      });
    });

    const seoLinks = document.querySelectorAll('.facet-seo-link-main');
    seoLinks.forEach(link => {
      if (!link.dataset.handlerAttached) {
        link.dataset.handlerAttached = "true";
        link.addEventListener('click', (e) => {
          e.preventDefault();

          const form = document.querySelector('#FacetFiltersForm') || document.querySelector('#FacetFiltersFormMobile');
          if (form) {
            const formData = new FormData(form);
            const currentParams = new URLSearchParams();

            for (const [key, value] of formData.entries()) {
              if (key !== 'utf8' && value !== '') {
                currentParams.append(key, value);
              }
            }

            const seoUrl = new URL(link.href, window.location.origin);

            currentParams.forEach((value, key) => {
              seoUrl.searchParams.set(key, value);
            });

            window.location.href = seoUrl.href;
          } else {
            window.location.href = link.href;
          }
        });
      }
    });
  }

  fixResetButtons() {
    const currentCollection = window.location.pathname.split("/collections/")[1]?.split("?")[0];
    const isOnFacetedCollection = currentCollection && currentCollection !== this.parentCollection;

    if (isOnFacetedCollection) {
      const resetButtons = this.querySelectorAll(".facets__reset");
      resetButtons.forEach((resetBtn) => {
        resetBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.navigateToCollection(`/collections/${this.parentCollection}`);
        });
      });
    }
  }

  recreateUrlBasedTag(filterData) {
    const containers = [
      { container: document.querySelector(".active-facets-desktop"), isMobile: false },
      { container: document.querySelector(".active-facets-mobile"), isMobile: true }
    ].filter(({ container }) => container);

    containers.forEach(({ container, isMobile }) => {
      const existsInThisContainer = Array.from(container.querySelectorAll("facet-remove"))
          .some((el) => {
            const text = el.textContent || el.innerText;
            return text.includes(filterData.label);
          });

      if (!existsInThisContainer) {
        const slugTag = document.createElement("facet-remove");
        slugTag.innerHTML = `
          <a href="/collections/${this.parentCollection}" class="active-facets__button active-facets__button--light js-url-based-filter" data-filter-slug="${filterData.filterSlug}">
            <span class="active-facets__button-inner button button--tertiary">
                ${this.getFilterLabel(filterData.filterEntry) || 'Filter'}: ${filterData.label}
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close-small" viewBox="0 0 12 13">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"/>
                </svg>
              </span>
              <span class="visually-hidden">Clear this filter</span>
            </span>
          </a>
        `;

        slugTag.querySelector("a").addEventListener("click", (e) => {
          e.preventDefault();
          const filterSlug = e.target.closest('a').dataset.filterSlug;
          const checkboxes = document.querySelectorAll(`input[data-filter-key="${filterSlug}"]`);
          checkboxes.forEach(cb => cb.checked = false);

          const currentUrl = new URL(window.location.href);
          const currentParams = new URLSearchParams(currentUrl.search);

          const entry = this.allFilters.find(f => f.slug === filterSlug);
          if (entry) {
            const paramToRemove = this.getFilterParamName(entry);
            if (paramToRemove) {
              currentParams.delete(paramToRemove);
            }
          }

          const preservedParams = currentParams.toString();
          const targetUrl = `/collections/${this.parentCollection}${preservedParams ? '?' + preservedParams : ''}`;
          this.navigateToCollection(targetUrl);
        });

        const removeAllButton = container.querySelector(".active-facets__button-remove")?.closest("facet-remove");
        if (removeAllButton) {
          container.insertBefore(slugTag, removeAllButton);
        } else {
          container.appendChild(slugTag);
        }
      }
    });
  }

  getSelectedSlugs() {
    return Array.from(document.querySelectorAll("input:checked[data-filter-key]"))
        .map((cb) => cb.dataset.filterKey);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search.slice(1);
      const previousPath = FacetFiltersForm.previousPath || currentPath;

      if (currentPath !== previousPath) {
        document.querySelectorAll('input[data-filter-key]').forEach(cb => {
          if (cb.checked) {
            cb.checked = false;
            cb.removeAttribute('checked');
            const label = cb.closest('label');
            if (label && label.classList.contains('active')) {
              label.classList.remove('active');
            }
          }
        });
        window.location.reload();
        return;
      }

      const searchParams = event.state ? event.state.searchParams : currentSearch;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    FacetFiltersForm.previousPath = window.location.pathname;
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById("ProductCountDesktop");
    const loadingSpinners = document.querySelectorAll(
        ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document.getElementById("ProductGridContainer").querySelector(".collection").classList.add("loading");
    if (countContainer) countContainer.classList.add("loading");
    if (countContainerDesktop) countContainerDesktop.classList.add("loading");
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
          : FacetFiltersForm.renderSectionFromFetch(url, event);
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = responseText;
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
        });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById("ProductGridContainer").innerHTML;
    document
        .getElementById("ProductGridContainer")
        .querySelectorAll(".scroll-trigger")
        .forEach((element) => {
          element.classList.add("scroll-trigger--cancel");
        });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, "text/html").getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
        ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
        "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
        "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });
    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);
    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains("mobile-facets__details")
            ? `.mobile-facets__close-button`
            : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        const isTextInput = event.target.getAttribute("type") === "text";
        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    const formInstance = document.querySelector("facet-filters-form");
    if (formInstance) {
      const currentPath = window.location.pathname;
      const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
      const parentCollection = formInstance.parentCollection;
      const isOnFacetedCollection = collectionHandle && collectionHandle !== parentCollection;

      if (isOnFacetedCollection) {
        formInstance.hasAutoChecked = false;
        formInstance.activeUrlFilters = [];
        formInstance.autoCheckUrlBasedFilters();
        formInstance.hasAutoChecked = true;
      }

      formInstance.initializeCheckboxes();
      formInstance.fixResetButtons();

      const urlBasedFilterTags = document.querySelectorAll(".js-url-based-filter");
      urlBasedFilterTags.forEach(tag => {
        if (!tag.dataset.handlerAttached) {
          tag.dataset.handlerAttached = "true";
          tag.addEventListener("click", (e) => {
            e.preventDefault();
            const filterSlug = tag.dataset.filterSlug;

            const checkboxes = document.querySelectorAll(`input[data-filter-key="${filterSlug}"]`);
            checkboxes.forEach(cb => cb.checked = false);

            const currentUrl = new URL(window.location.href);
            const currentParams = new URLSearchParams(currentUrl.search);

            const entry = formInstance.allFilters.find(f => f.slug === filterSlug);
            if (entry) {
              const paramToRemove = formInstance.getFilterParamName(entry);
              if (paramToRemove) {
                currentParams.delete(paramToRemove);
              }
            }

            const preservedParams = currentParams.toString();
            const targetUrl = `/collections/${formInstance.parentCollection}${preservedParams ? '?' + preservedParams : ''}`;
            formInstance.navigateToCollection(targetUrl);
          });
        }
      });
    }

    const seoLinks = document.querySelectorAll('.facet-seo-link-main');
    seoLinks.forEach(link => {
      if (!link.dataset.handlerAttached) {
        link.dataset.handlerAttached = "true";
        link.addEventListener('click', (e) => {
          e.preventDefault();

          const form = document.querySelector('#FacetFiltersForm') || document.querySelector('#FacetFiltersFormMobile');
          if (form) {
            const formData = new FormData(form);
            const currentParams = new URLSearchParams();

            for (const [key, value] of formData.entries()) {
              if (key !== 'utf8' && value !== '') {
                currentParams.append(key, value);
              }
            }

            const seoUrl = new URL(link.href, window.location.origin);

            currentParams.forEach((value, key) => {
              seoUrl.searchParams.set(key, value);
            });

            window.location.href = seoUrl.href;
          } else {
            window.location.href = link.href;
          }
        });
      }
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets-mobile", ".active-facets-desktop"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);

    const formInstance = document.querySelector("facet-filters-form");
    if (formInstance?.activeUrlFilters && formInstance.activeUrlFilters.length > 0) {
      const currentPath = window.location.pathname;
      const parentCollection = formInstance.parentCollection;
      const isOnFacetedCollection = currentPath.includes(`/collections/`) &&
          currentPath !== `/collections/${parentCollection}`;

      if (isOnFacetedCollection) {
        const containers = [".active-facets-mobile", ".active-facets-desktop"];
        containers.forEach(containerSelector => {
          const container = document.querySelector(containerSelector);
          if (!container) return;

          formInstance.activeUrlFilters.forEach(filterData => {
            const shopifyTags = container.querySelectorAll('facet-remove a:not(.js-url-based-filter)');
            shopifyTags.forEach(tag => {
              const tagText = tag.textContent || tag.innerText;
              if (tagText.includes(filterData.label)) {
                tag.closest('facet-remove').remove();
              }
            });
          });
        });

        formInstance.activeUrlFilters.forEach(filterData => {
          formInstance.recreateUrlBasedTag(filterData);
        });
      }
    }
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-facets__open", ".mobile-facets__count", ".sorting"];
    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
    document.getElementById("FacetFiltersFormMobile").closest("menu-drawer").bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }
    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");
    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }
    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector("show-more-button .label-show-more.hidden"));
      if (isShowingMore) {
        sourceWrapElement
            .querySelectorAll(".facets__item.hidden")
            .forEach((hiddenItem) => hiddenItem.classList.replace("hidden", "show-more-item"));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    FacetFiltersForm.previousPath = window.location.pathname;
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (this.isRedirecting) {
      console.log("🚫 Stopping AJAX - redirect in progress");
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(event.target.closest("form"));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest("form").id === "FacetFiltersFormMobile";
      sortFilterForms.forEach((form) => {
        if (!isMobile && ["FacetSortForm", "FacetFiltersForm", "FacetSortDrawerForm"].includes(form.id)) {
          forms.push(this.createSearchParams(form));
        } else if (isMobile && form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
        event.currentTarget.href.indexOf("?") == -1
            ? ""
            : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.initFormCleaner();
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();